/**
 *
 * v.1.0  02/02/2025  Inicial
 * v.1.1  08/09/2025  Modificado para pintar directamente la barra superior
 *                    Añadidas propiedades onHoraClick, onHoraDobleClick
 * v.1.2  29/09/2025  Ajuste: scroll automático a la hora de "fecha"
 **/

import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { darkenColor, getContrastingTextColor } from "../../../dental/utils/ColorUtils";
import { X, CheckCircle, PhoneOff, MessageCircle } from "lucide-react";
import CalendarioToolbar from "./CalendarioToolbar";

// Textos en español
const messages = {
    allDay: 'Todo el día',
    previous: 'Anterior',
    next: 'Siguiente',
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    work_week: "Semana laboral",
    day: 'Día',
    agenda: 'Agenda',
    date: 'Fecha',
    time: 'Hora',
    event: 'Evento',
    showMore: (total) => `+ Ver más (${total})`,
};

const WhatsAppConfirmado = ({ size = 24 }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <CheckCircle size={size} color="green" title="CONFIRMADO" />
        <MessageCircle size={size} color="blue" title="WHATSAPP" />
    </div>
);

const iconoSize = 24;
const estadoIconos = {
    1: { icono: <CheckCircle size={iconoSize} color="green" title="AVISADO" />, titulo: "AVISADO" },
    2: { icono: <PhoneOff size={iconoSize} color="red" title="NO CONTESTA" />, titulo: "NO CONTESTA" },
    3: { icono: <MessageCircle size={iconoSize} color="blue" title="WHATSAPP" />, titulo: "WHATSAPP" },
    4: { icono: <WhatsAppConfirmado size={iconoSize} color="green" title="WHATSAPP CONFIRMADO" />, titulo: "WHATSAPP CONFIRMADO" },
};

class Calendario extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            eventos: props.eventos || [],
            fechaActual: props.fecha || new Date(),
        };

        moment.locale('es');
        this.localizer = momentLocalizer(moment);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const newState = {};

        if (nextProps.eventos !== prevState.eventos) {
            newState.eventos = nextProps.eventos;
        }

        if (
            nextProps.fecha &&
            (!prevState.fechaActual || nextProps.fecha.getTime() !== prevState.fechaActual.getTime())
        ) {
            newState.fechaActual = nextProps.fecha;
        }

        return Object.keys(newState).length > 0 ? newState : null;
    }

    customEvent = ({ event }) => {
        const handlePacienteClick = (e) => {
            const { onPacienteClick } = this.props;
            e.stopPropagation();
            onPacienteClick && onPacienteClick(event);
        };
        const handleDeleteClick = (e) => {
            const { onEventoBorrar } = this.props;
            e.stopPropagation();
            onEventoBorrar && onEventoBorrar(event);
        };

        let iconoInfo = estadoIconos[event.avisoestado];
        let pacienteInfo = event.pacienteCodigo ? "(" + event.pacienteCodigo.codigoid + ") " + event.pacienteCodigo.nombre : "";

        return (
            <span style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                    onClick={handleDeleteClick}
                    style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        cursor: "pointer",
                        zIndex: 10,
                    }}
                >
                    <X size={16} color="red" />
                </div>

                {event.pacienteCodigo && <div onClick={handlePacienteClick}><strong>{pacienteInfo}</strong></div>}
                {event.doctorCodigo && <small>{event.doctorCodigo.nombre}</small>}
                {event.texto && event.texto}
                {iconoInfo &&
                    <div style={{ position: "absolute", bottom: "2px", right: "2px" }}>
                        {iconoInfo.icono} <span>{iconoInfo.titulo}</span>
                    </div>
                }
            </span>
        );
    };

    eventStyleGetter = (event) => {
        //console.log("event: ", event);
        const backgroundColor = event.doctorCodigo.color && event.doctorCodigo.color !== "0"
            ? event.doctorCodigo.color
            : '#3174ad';
        const borderColor = darkenColor(backgroundColor, 30);
        const textColor = getContrastingTextColor(backgroundColor);
        return {
            style: {
                background: backgroundColor,
                border: `2px solid ${borderColor}`,
                borderRadius: '6px',
                opacity: 0.9,
                color: textColor,
                display: 'block',
                paddingLeft: '4px',
            },
        };
    };

    handleSelectEvent = (event) => {
        const { onEventoSeleccionado } = this.props;
        onEventoSeleccionado && onEventoSeleccionado(event);
    };

    handleNavigate = (newDate) => {
        const { onFechaActualModificada } = this.props;
        this.setState({ fechaActual: newDate });
        onFechaActualModificada && onFechaActualModificada(newDate);
    };

    handleSelectSlot = (slotInfo) => {
        const { onHoraClick, onHoraDobleClick } = this.props;
        const rango = { start: slotInfo.start, end: slotInfo.end };
        if (slotInfo.action === "click" && onHoraClick) {
            onHoraClick(rango);
        } else if (slotInfo.action === "doubleClick" && onHoraDobleClick) {
            onHoraDobleClick(rango);
        }
    };

    render() {
        const { eventos, fechaActual } = this.state;

        console.log("RENDER CALENDARIO");
        console.log("eventos: ", eventos);

        return (
            <div style={{ height: '80vh' }}>
                <Calendar
                    key={fechaActual ? fechaActual.getTime() : 'calendar'} // 👈 fuerza remount al cambiar fecha
                    date={fechaActual}                 // 👈 día mostrado
                    localizer={this.localizer}
                    events={eventos}
                    defaultView="day"
                    views={{ month: true, week: true, work_week: true, day: true }}
                    step={30}
                    timeslots={1}
                    min={new Date(2025, 0, 18, 8, 0)}
                    max={new Date(2025, 0, 18, 22, 0)}
                    style={{ height: '100%' }}
                    messages={messages}
                    onSelectEvent={this.handleSelectEvent}
                    components={{
                        event: this.customEvent,
                        toolbar: CalendarioToolbar,
                    }}
                    eventPropGetter={this.eventStyleGetter}
                    dayLayoutAlgorithm="no-overlap"
                    scrollToTime={fechaActual}         // 👈 scroll inicial
                    onNavigate={this.handleNavigate}
                    selectable
                    onSelectSlot={this.handleSelectSlot}
                />
            </div>
        );
    }
}

Calendario.propTypes = {
    eventos: PropTypes.array,
    onEventoSeleccionado: PropTypes.func,
    onPacienteClick: PropTypes.func,
    onEventoBorrar: PropTypes.func,
    onFechaActualModificada: PropTypes.func,
    onHoraClick: PropTypes.func,
    onHoraDobleClick: PropTypes.func,
    fecha: PropTypes.object,
};

export default Calendario;
